/**
 * Locale 元数据 —— 供 app 渲染语言切换器。
 * 顺序：英语第一公民（`en` 居首，是基准/fallback）。
 *
 * @typedef {'en'|'zh'|'ja'|'ko'|'es'|'pt'} LocaleCode
 * @typedef {{ code:LocaleCode, label:string, nativeLabel:string, dir:'ltr'|'rtl' }} LocaleMeta
 */

/**
 * 支持语言枚举。与 api-sdk 的 `x-locale` 取值、后端约定共用同一套枚举。
 * `en` 为基准/fallback（英语第一公民）。
 * @type {LocaleCode[]}
 */
export const SUPPORTED_LOCALES = ['en', 'zh', 'ja', 'ko', 'es', 'pt']

/** 默认 locale（缺省初值与 fallback）。 @type {LocaleCode} */
export const DEFAULT_LOCALE = 'en'

/**
 * 6 语言元数据。`label` 为英文显示名（app 在英语 UI 下用），
 * `nativeLabel` 为该语言自称（语言切换器优先展示），`dir` 书写方向。
 * 当前 6 语言均 `ltr`，结构预留 `rtl` 以便后续接入阿拉伯语等。
 * @type {LocaleMeta[]}
 */
export const LOCALE_META = [
  { code: 'en', label: 'English', nativeLabel: 'English', dir: 'ltr' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文', dir: 'ltr' },
  { code: 'ja', label: 'Japanese', nativeLabel: '日本語', dir: 'ltr' },
  { code: 'ko', label: 'Korean', nativeLabel: '한국어', dir: 'ltr' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español', dir: 'ltr' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', dir: 'ltr' },
]

/**
 * 判断给定值是否为受支持的 locale 码。
 * @param {unknown} code
 * @returns {code is LocaleCode}
 */
export function isSupportedLocale(code) {
  return typeof code === 'string' && SUPPORTED_LOCALES.includes(/** @type {LocaleCode} */ (code))
}

/**
 * 把内容/配音资源上出现的 locale 字符串（大小写、BCP47 变体不一）归一成本仓库统一认的
 * 那份枚举——前端选素材/选音色、后端选 TTS 发音语言，都得是同一个判断，不能各写一份。
 * 中文归一到 `'zh-CN'`（历史上媒体/配音资源按 zh-CN 分包），其余归一到裸码。
 * @param {unknown} value
 * @returns {'zh-CN'|LocaleCode|null} 空值（null/undefined/''）归一成 null；无法识别则抛错。
 */
export function normalizeContentLocale(value) {
  if (value === undefined || value === null || value === '') return null
  if (typeof value !== 'string') throw new Error(`Content locale must be a string: ${String(value)}`)
  const normalized = value.trim().toLowerCase()
  if (normalized === 'zh' || normalized === 'zh-cn' || normalized === 'zh-hans' || normalized.startsWith('zh-hans-')) return 'zh-CN'
  if (normalized === 'pt' || normalized === 'pt-br' || normalized.startsWith('pt-br-')) return 'pt'
  if (normalized === 'en' || normalized.startsWith('en-')) return 'en'
  if (normalized === 'ja' || normalized.startsWith('ja-')) return 'ja'
  if (normalized === 'ko' || normalized.startsWith('ko-')) return 'ko'
  if (normalized === 'es' || normalized.startsWith('es-')) return 'es'
  throw new Error(`Unsupported content locale: ${value}`)
}
